<script setup lang="ts">
import type { BlogPost } from '~/composables/useBlog'
import { logApiError } from '~/utils/logger'
import { sanitizeHtml } from '~/utils/sanitize'

definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'Nieuws',
    subtitle: 'Blijf op de hoogte van het laatste nieuws van \'t SpeelFabrik',
    icon: 'bx-news',
    iconColor: 'primary',
  },
})

// SEO Configuration
const { setSeoMeta } = useSeo()

setSeoMeta({
  title: 'Nieuws',
  description: 'Blijf op de hoogte van het laatste nieuws en updates van \'t SpeelFabrik. Lees over onze kampen, activiteiten en meer.',
  path: '/nieuws',
  keywords: 'nieuws, blog, updates, speelfabrik, kampen, activiteiten',
})

// Composable
const { getPublishedPosts } = useBlog()

// State
const posts = ref<BlogPost[]>([])
const loading = ref(true)
const expandedPosts = ref<Set<string>>(new Set())
const collapsedHeight = 150 // px

// Lightbox state
const lightboxOpen = ref(false)
const lightboxImage = ref('')
const lightboxTitle = ref('')

function openLightbox(imageUrl: string, title: string) {
  lightboxImage.value = imageUrl
  lightboxTitle.value = title
  lightboxOpen.value = true
}

// Fetch published blog posts
async function fetchPosts() {
  loading.value = true
  try {
    const { data } = await getPublishedPosts({
      sortBy: 'published_at',
      sortOrder: 'desc',
      limit: 50,
    })

    posts.value = data
  }
  catch (error) {
    logApiError('/api/blog', error, { operation: 'fetchPosts' })
  }
  finally {
    loading.value = false
  }
}

function toggleExpanded(postId: string) {
  if (expandedPosts.value.has(postId)) {
    expandedPosts.value.delete(postId)
  }
  else {
    expandedPosts.value.add(postId)
  }
}

function isExpanded(postId: string) {
  return expandedPosts.value.has(postId)
}

function formatDate(dateString: string | null) {
  if (!dateString)
    return ''

  return new Date(dateString).toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// Lifecycle
onMounted(() => {
  fetchPosts()
})
</script>

<template>
  <div class="nieuws-page">
    <!-- Wave Transition to Red Section -->
    <div class="wave-transition-red-top">
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="wave-svg-red wave-red-1">
        <path d="M0,0 L0,0 L1200,0 L1200,60 C1000,100 800,20 600,70 C400,120 200,40 0,80 Z" />
      </svg>
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="wave-svg-red wave-red-2">
        <path d="M0,0 L0,0 L1200,0 L1200,50 C950,10 750,90 500,40 C300,100 150,20 0,60 Z" />
      </svg>
    </div>

    <!-- Blog Posts Section -->
    <section class="nieuws-content py-16">
      <!-- Decorative background gears -->
      <div class="gear-decoration gear-1" />
      <div class="gear-decoration gear-2" />
      <div class="gear-decoration gear-3" />
      <div class="gear-decoration gear-4" />

      <VContainer>
        <!-- Loading State -->
        <div
          v-if="loading"
          class="text-center py-16"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="64"
          />
          <p class="text-body-1 mt-4">
            Berichten laden...
          </p>
        </div>

        <!-- Empty State -->
        <VRow
          v-else-if="!posts.length"
          justify="center"
        >
          <VCol
            cols="12"
            md="6"
            class="text-center py-16"
          >
            <VIcon
              icon="bx-news"
              size="80"
              color="secondary"
              class="mb-4"
            />
            <h3 class="text-h5 mb-2">
              Nog geen nieuwsberichten
            </h3>
            <p class="text-body-1 text-medium-emphasis">
              Binnenkort delen we hier het laatste nieuws en updates.
            </p>
          </VCol>
        </VRow>

        <!-- Posts List -->
        <VRow v-else>
          <VCol
            v-for="post in posts"
            :key="post.id"
            cols="12"
            md="6"
            lg="4"
          >
            <VCard
              class="post-card h-100"
              elevation="2"
            >
              <!-- Featured Image -->
              <VImg
                v-if="post.featured_image_url"
                :src="post.featured_image_url"
                :alt="post.title"
                height="450"
                cover
                class="post-image cursor-pointer"
                @click="openLightbox(post.featured_image_url!, post.title)"
              />

              <VCardText class="pa-6">
                <!-- Post Header -->
                <div class="d-flex flex-wrap align-center gap-2 mb-3">
                  <VChip
                    v-if="post.category"
                    color="primary"
                    size="small"
                    variant="tonal"
                  >
                    {{ post.category }}
                  </VChip>
                  <span class="text-caption text-medium-emphasis">
                    <VIcon
                      icon="bx-calendar"
                      size="14"
                      class="me-1"
                    />
                    {{ formatDate(post.published_at) }}
                  </span>
                </div>

                <!-- Title -->
                <h2 class="text-h5 mb-4">
                  {{ post.title }}
                </h2>

                <!-- Content with Collapse -->
                <div
                  class="post-content-wrapper"
                  :class="{ 'is-expanded': isExpanded(post.id) }"
                  :style="{
                    maxHeight: isExpanded(post.id) ? 'none' : `${collapsedHeight}px`,
                  }"
                >
                  <div
                    class="post-content prose"
                    v-html="sanitizeHtml(post.content)"
                  />

                  <!-- Fade overlay when collapsed -->
                  <div
                    v-if="!isExpanded(post.id)"
                    class="content-fade"
                  />
                </div>

                <!-- Expand/Collapse Button -->
                <div class="text-center mt-4">
                  <VBtn
                    variant="text"
                    color="primary"
                    @click="toggleExpanded(post.id)"
                  >
                    <VIcon
                      :icon="isExpanded(post.id) ? 'bx-chevron-up' : 'bx-chevron-down'"
                      start
                    />
                    {{ isExpanded(post.id) ? 'Minder tonen' : 'Lees meer' }}
                  </VBtn>
                </div>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VContainer>
    </section>

    <!-- Wave Transition from Red Section -->
    <div class="wave-transition-red-bottom">
      <svg viewBox="0 0 1200 140" preserveAspectRatio="none" class="wave-svg-red-bottom wave-red-bottom-1">
        <path d="M0,60 C200,20 400,100 600,50 C800,0 1000,80 1200,40 L1200,140 L0,140 Z" />
      </svg>
      <svg viewBox="0 0 1200 140" preserveAspectRatio="none" class="wave-svg-red-bottom wave-red-bottom-2">
        <path d="M0,40 C150,90 300,10 500,60 C750,110 950,30 1200,70 L1200,140 L0,140 Z" />
      </svg>
    </div>

    <!-- Lightbox Dialog -->
    <VDialog
      v-model="lightboxOpen"
      max-width="1200"
    >
      <VCard class="lightbox-card">
        <VImg
          :src="lightboxImage"
          :alt="lightboxTitle"
          max-height="80vh"
          contain
        />
        <VBtn
          icon
          variant="text"
          class="lightbox-close"
          @click="lightboxOpen = false"
        >
          <VIcon icon="bx-x" />
        </VBtn>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable selector-pseudo-class-no-unknown, liberty/use-logical-spec */
.nieuws-page {
  background-color: rgb(var(--v-theme-surface));
  min-block-size: 100vh;
}

.nieuws-content {
  position: relative;
  overflow: hidden;
  background-color: #f26f63;
  color: #f6ebd7;
}

@keyframes wave-float-1 {
  0%,
  100% {
    transform: translateY(0) scaleX(1);
  }

  50% {
    transform: translateY(-8px) scaleX(1.02);
  }
}

@keyframes wave-float-2 {
  0%,
  100% {
    transform: translateY(0) scaleX(1);
  }

  50% {
    transform: translateY(-12px) scaleX(1.01);
  }
}

.wave-transition-red-top {
  position: relative;
  z-index: 5;
  overflow: hidden;
  background-color: #f26f63;
  block-size: 120px;
  margin-block-end: -1px;

  .wave-svg-red {
    position: absolute;
    block-size: calc(100% + 24px);
    inline-size: 110%;
    inset-block-start: 0;
    inset-inline-start: -5%;

    path {
      fill: rgb(var(--v-theme-background));
    }
  }

  .wave-red-1 {
    animation: wave-float-1 7s ease-in-out infinite;
    opacity: 0.7;
  }

  .wave-red-2 {
    animation: wave-float-2 9s ease-in-out infinite;
    opacity: 0.9;
  }
}

.wave-transition-red-bottom {
  position: relative;
  z-index: 5;
  overflow: hidden;
  background-color: #f26f63;
  block-size: 140px;
  margin-block-start: -1px;

  .wave-svg-red-bottom {
    position: absolute;
    block-size: calc(100% + 24px);
    inline-size: 110%;
    inset-block-end: 0;
    inset-inline-start: -5%;

    path {
      fill: rgb(var(--v-theme-background));
    }
  }

  .wave-red-bottom-1 {
    animation: wave-float-1 8s ease-in-out infinite;
    opacity: 0.7;
  }

  .wave-red-bottom-2 {
    animation: wave-float-2 10s ease-in-out infinite;
    opacity: 0.9;
  }
}

.gear-decoration {
  position: absolute;
  border-radius: 50%;
  opacity: 0.05;
  pointer-events: none;
}

.gear-1 {
  background:
    radial-gradient(circle, #f6ebd7 20%, transparent 20%),
    radial-gradient(circle, transparent 20%, #f6ebd7 20%, #f6ebd7 30%, transparent 30%);
  background-position:
    0 0,
    50px 50px;
  background-size: 50px 50px;
  block-size: 300px;
  inline-size: 300px;
  inset-block-start: 10%;
  inset-inline-start: -5%;
}

.gear-2 {
  background:
    radial-gradient(circle, #f6ebd7 20%, transparent 20%),
    radial-gradient(circle, transparent 20%, #f6ebd7 20%, #f6ebd7 30%, transparent 30%);
  background-position:
    0 0,
    35px 35px;
  background-size: 35px 35px;
  block-size: 200px;
  inline-size: 200px;
  inset-block-start: 60%;
  inset-inline-end: 10%;
}

.gear-3 {
  background:
    radial-gradient(circle, #f6ebd7 20%, transparent 20%),
    radial-gradient(circle, transparent 20%, #f6ebd7 20%, #f6ebd7 30%, transparent 30%);
  background-position:
    0 0,
    25px 25px;
  background-size: 25px 25px;
  block-size: 150px;
  inline-size: 150px;
  inset-block-end: 20%;
  inset-inline-start: 15%;
}

.gear-4 {
  background:
    radial-gradient(circle, #f6ebd7 20%, transparent 20%),
    radial-gradient(circle, transparent 20%, #f6ebd7 20%, #f6ebd7 30%, transparent 30%);
  background-position:
    0 0,
    30px 30px;
  background-size: 30px 30px;
  block-size: 180px;
  inline-size: 180px;
  inset-block-start: 40%;
  inset-inline-end: -3%;
}

.post-card {
  overflow: hidden;
  border-radius: 16px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 8px 32px rgb(0 0 0 / 12%);
    transform: translateY(-4px);
  }
}

.post-image {
  border-end-end-radius: 0;
  border-end-start-radius: 0;
}

.post-content-wrapper {
  position: relative;
  overflow: hidden;
  transition: max-block-size 0.5s ease;

  &:not(.is-expanded) {
    .content-fade {
      position: absolute;
      background: linear-gradient(to bottom, transparent 0%, rgb(var(--v-theme-surface)) 100%);
      block-size: 80px;
      inline-size: 100%;
      inset-block-end: 0;
      inset-inline-start: 0;
      pointer-events: none;
    }
  }
}

.post-content {
  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    font-weight: 600;
    margin-block: 1em 0.5em;
  }

  :deep(p) {
    line-height: 1.7;
    margin-block-end: 1em;
  }

  :deep(ul),
  :deep(ol) {
    margin-block-end: 1em;
    padding-inline-start: 1.5em;
  }

  :deep(li) {
    margin-block-end: 0.5em;
  }

  :deep(img) {
    border-radius: 8px;
    block-size: auto;
    max-inline-size: 100%;
  }

  :deep(a) {
    color: rgb(var(--v-theme-primary));
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  :deep(blockquote) {
    border-inline-start: 4px solid rgb(var(--v-theme-primary));
    font-style: italic;
    margin-block: 1em;
    margin-inline: 0;
    padding-inline-start: 1em;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.lightbox-card {
  position: relative;
  background-color: transparent;
  box-shadow: none;
}

.lightbox-close {
  position: absolute;
  color: white;
  inset-block-start: 8px;
  inset-inline-end: 8px;
}
</style>
